/**
 * PwaInstallPrompt
 *
 * Shows a banner inviting the user to install the PDV app when the
 * `beforeinstallprompt` event fires. Only renders on POS pages.
 *
 * The user may dismiss the banner (stored in sessionStorage for the
 * current browsing session) or trigger the native install dialog.
 */

import { useState, useEffect, useCallback } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DISMISSED_KEY = 'nexora-pwa-install-dismissed';

export function PwaInstallPrompt(): JSX.Element | null {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [installing, setInstalling] = useState(false);

  // -----------------------------------------------------------------------
  // Check local state on mount
  // -----------------------------------------------------------------------
  useEffect(() => {
    // Detect if already installed (standalone mode)
    if (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as unknown as Record<string, unknown>)['standalone']
    ) {
      setIsInstalled(true);
      return;
    }

    // Check if the user dismissed the prompt this session
    const dismissed = sessionStorage.getItem(DISMISSED_KEY);
    if (dismissed === 'true') {
      return;
    }
  }, []);

  // -----------------------------------------------------------------------
  // Listen for beforeinstallprompt
  // -----------------------------------------------------------------------
  useEffect(() => {
    function handleBeforeInstallPrompt(event: BeforeInstallPromptEvent): void {
      // Prevent the default mini-infobar
      event.preventDefault();
      setInstallPrompt(event);
      setShowBanner(true);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // -----------------------------------------------------------------------
  // Listen for app install
  // -----------------------------------------------------------------------
  useEffect(() => {
    function handleAppInstalled(): void {
      setIsInstalled(true);
      setShowBanner(false);
      setInstallPrompt(null);
    }

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleInstall = useCallback(async () => {
    if (!installPrompt) return;

    setInstalling(true);

    try {
      await installPrompt.prompt();
      const result = await installPrompt.userChoice;

      if (result.outcome === 'accepted') {
        setIsInstalled(true);
        setShowBanner(false);
      }
    } catch {
      // Prompt failed (e.g., not in secure context or already installed)
    } finally {
      setInstalling(false);
      setInstallPrompt(null);
    }
  }, [installPrompt]);

  const handleDismiss = useCallback(() => {
    sessionStorage.setItem(DISMISSED_KEY, 'true');
    setShowBanner(false);
    setInstallPrompt(null);
  }, []);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (!showBanner || isInstalled) {
    return null;
  }

  return (
    <div className="pwa-install-banner" role="alert" aria-live="polite">
      <div className="pwa-install-content">
        <div className="pwa-install-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="3" width="18" height="18" rx="3" />
            <line x1="12" y1="8" x2="12" y2="16" />
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
        </div>

        <div className="pwa-install-text">
          <strong>Instale o Nexora PDV</strong>
          <span>Acesse rapidamente com um icone na tela inicial</span>
        </div>

        <div className="pwa-install-actions">
          <button
            type="button"
            className="pwa-install-btn pwa-install-btn--secondary"
            onClick={handleDismiss}
            disabled={installing}
            aria-label="Fechar aviso de instalacao"
          >
            Agora nao
          </button>

          <button
            type="button"
            className="pwa-install-btn pwa-install-btn--primary"
            onClick={handleInstall}
            disabled={installing}
          >
            {installing ? 'Instalando...' : 'Instalar'}
          </button>
        </div>
      </div>

      <style>{`
        .pwa-install-banner {
          position: fixed;
          bottom: 1rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 9999;
          width: calc(100% - 2rem);
          max-width: 440px;
          background: #ffffff;
          border: 1px solid #e0e3e5;
          border-radius: 12px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
          padding: 0.875rem;
          animation: pwa-slide-up 0.35s ease-out;
        }

        @keyframes pwa-slide-up {
          from {
            opacity: 0;
            transform: translateY(1rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .pwa-install-content {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .pwa-install-icon {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          background: #d6e3ff;
          border-radius: 12px;
          color: #002045;
        }

        .pwa-install-text {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
        }

        .pwa-install-text strong {
          font-size: 0.875rem;
          color: #1f1f1f;
        }

        .pwa-install-text span {
          font-size: 0.75rem;
          color: #5f6368;
        }

        .pwa-install-actions {
          display: flex;
          gap: 0.5rem;
          flex-shrink: 0;
        }

        .pwa-install-btn {
          padding: 0.375rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8125rem;
          font-weight: 500;
          cursor: pointer;
          border: none;
          transition: background 0.15s, opacity 0.15s;
          white-space: nowrap;
        }

        .pwa-install-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .pwa-install-btn--secondary {
          background: transparent;
          color: #5f6368;
        }

        .pwa-install-btn--secondary:hover:not(:disabled) {
          background: #f1f3f4;
        }

        .pwa-install-btn--primary {
          background: #002045;
          color: #ffffff;
        }

        .pwa-install-btn--primary:hover:not(:disabled) {
          background: #1765cc;
        }

        @media (prefers-color-scheme: dark) {
          .pwa-install-banner {
            background: #2d2d2d;
            border-color: #191c1e;
          }

          .pwa-install-text strong {
            color: #e8eaed;
          }

          .pwa-install-text span {
            color: #9aa0a6;
          }

          .pwa-install-btn--secondary {
            color: #9aa0a6;
          }

          .pwa-install-btn--secondary:hover:not(:disabled) {
            background: #3c4043;
          }
        }
      `}</style>
    </div>
  );
}
